import React, { useEffect, useState } from "react";
import { FaPlay, FaPen, FaTrashAlt } from "react-icons/fa";
import fileIcon from "@/assets/icons/fileIcon.svg";
import { ApiResource } from "../interface";
import { extractYoutubeId, extractVimeoId, fetchVimeoMeta, extractLoomId, fetchLoomMeta } from "../utils";

interface ResourceCardProps {
    resource: ApiResource;
    onPlay: (youtubeId: string) => void;
    isAdmin?: boolean;
    onEdit?: (resource: ApiResource) => void;
    onDelete?: (resource: ApiResource) => void;
}

const FILE_TYPE_STYLES: Record<string, { tint: string; badge: string }> = {
    PDF: { tint: "#FBE9E7", badge: "#E5484D" },
    DOC: { tint: "#E3EDFB", badge: "#2B579A" },
    DOCX: { tint: "#E3EDFB", badge: "#2B579A" },
    XLS: { tint: "#E3F5E9", badge: "#1D8A4B" },
    XLSX: { tint: "#E3F5E9", badge: "#1D8A4B" },
    PPT: { tint: "#FCEEE3", badge: "#D35127" },
    PPTX: { tint: "#FCEEE3", badge: "#D35127" },
};
const DEFAULT_FILE_STYLE = { tint: "#F5F5F5", badge: "#9CA3AF" };

const getFileExtension = (url: string): string => {
    const match = url.split("?")[0].match(/\.([a-zA-Z0-9]+)$/);
    return match ? match[1].toUpperCase() : "FILE";
};

const ResourceCard: React.FC<ResourceCardProps> = ({ resource, onPlay, isAdmin, onEdit, onDelete }) => {
    const youtubeId = resource.type === "Video" ? extractYoutubeId(resource.url) : null;
    const vimeoId = resource.type === "Video" && !youtubeId ? extractVimeoId(resource.url) : null;
    const loomId = resource.type === "Video" && !youtubeId && !vimeoId ? extractLoomId(resource.url) : null;
    const [oembedThumbnail, setOembedThumbnail] = useState<string | null>(null);

    useEffect(() => {
        if (!vimeoId && !loomId) {
            setOembedThumbnail(null);
            return;
        }
        let cancelled = false;
        const fetchMeta = vimeoId ? fetchVimeoMeta : fetchLoomMeta;
        fetchMeta(resource.url).then((meta) => {
            if (!cancelled) setOembedThumbnail(meta?.thumbnailUrl ?? null);
        });
        return () => {
            cancelled = true;
        };
    }, [vimeoId, loomId, resource.url]);

    const thumbnailUrl = youtubeId
        ? `https://img.youtube.com/vi/${youtubeId}/hqdefault.jpg`
        : oembedThumbnail;

    const fileExt = resource.type === "Document" ? getFileExtension(resource.url) : null;
    const fileStyle = fileExt ? (FILE_TYPE_STYLES[fileExt] ?? DEFAULT_FILE_STYLE) : DEFAULT_FILE_STYLE;

    const handleClick = () => {
        if (resource.type === "Video" && youtubeId) {
            onPlay(youtubeId);
        } else {
            window.open(resource.url, "_blank", "noopener,noreferrer");
        }
    };

    return (
        <div
            className="bg-white border border-gray-100 rounded-xl overflow-hidden hover:border-primary-red transition-colors cursor-pointer"
            onClick={handleClick}
        >
            {resource.type === "Video" ? (
                <div
                    className="relative aspect-[16/10] bg-cover bg-center bg-p-gray group"
                    style={{
                        backgroundImage: thumbnailUrl ? `url(${thumbnailUrl})` : undefined,
                    }}
                >
                    <div className="absolute inset-0 bg-gradient-to-b from-black/5 via-black/10 to-black/40" />
                    <span className="absolute left-2 top-2 bg-white/90 text-[9px] font-bold uppercase tracking-wide px-2 py-0.5 rounded">
                        {resource.category}
                    </span>
                    {resource.duration && (
                        <span className="absolute right-2 bottom-2 bg-black/70 text-white text-[10px] font-medium px-2 py-0.5 rounded">
                            {resource.duration}
                        </span>
                    )}
                    <div className="absolute inset-0 flex items-center justify-center">
                        <span className="w-9 h-9 rounded-full bg-white/95 flex items-center justify-center shadow group-hover:scale-105 transition-transform">
                            <FaPlay className="text-primary-red ml-0.5" size={12} />
                        </span>
                    </div>
                </div>
            ) : (
                <div
                    className="relative aspect-[16/10] flex items-center justify-center"
                    style={{ backgroundColor: fileStyle.tint }}
                >
                    <span className="absolute left-2 top-2 bg-white/90 text-[9px] font-bold uppercase tracking-wide px-2 py-0.5 rounded z-10">
                        {resource.category}
                    </span>
                    {resource.size && (
                        <span className="absolute right-2 bottom-2 bg-black/70 text-white text-[10px] font-medium px-2 py-0.5 rounded z-10">
                            {resource.size}
                        </span>
                    )}
                    <div className="relative" style={{ width: 64, height: 80 }}>
                        <img src={fileIcon} alt="" className="w-full h-full drop-shadow-sm" />
                        <div
                            className="absolute flex items-center justify-center rounded font-bold text-white"
                            style={{
                                left: 14,
                                top: 48,
                                width: 36,
                                height: 16,
                                backgroundColor: fileStyle.badge,
                                fontSize: 9,
                                letterSpacing: 0.5,
                            }}
                        >
                            {fileExt}
                        </div>
                    </div>
                </div>
            )}
            <div className="p-3">
                <div className="flex items-start justify-between gap-2">
                    <p className="text-sm font-semibold line-clamp-2">{resource.name}</p>
                    {!resource.is_active && (
                        <span className="shrink-0 text-[9px] font-semibold text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                            Inactive
                        </span>
                    )}
                </div>
                <p className="text-xs text-gray-400 mt-1 line-clamp-1">{resource.description}</p>

                {isAdmin && (onEdit || onDelete) && (
                    <div className="flex items-center gap-3 mt-2 pt-2 border-t border-gray-100">
                        {onEdit && (
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onEdit(resource);
                                }}
                                className="flex items-center gap-1.5 text-xs font-medium text-gray-500 hover:text-primary-red transition-colors"
                            >
                                <FaPen size={10} /> Edit
                            </button>
                        )}
                        {onDelete && (
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onDelete(resource);
                                }}
                                className="flex items-center gap-1.5 text-xs font-medium text-gray-500 hover:text-red-600 transition-colors"
                            >
                                <FaTrashAlt size={10} /> Delete
                            </button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ResourceCard;
